import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Activity, 
  Database, 
  DollarSign, 
  Users, 
  Shield, 
  Gauge,
  RefreshCw
} from 'lucide-react';

const SystemHealthDashboard = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const fetchHealthData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('qapi-token');
      const response = await fetch(`${API_URL}/api/system-health/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch health data');
      
      const data = await response.json();
      setHealthData(data);
      setLastUpdated(new Date().toLocaleTimeString());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealthData();
    const interval = setInterval(fetchHealthData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent':
        return 'text-emerald-500';
      case 'good':
        return 'text-blue-500';
      case 'needs_attention':
        return 'text-amber-500';
      default:
        return 'text-slate-400';
    }
  };

  const getCertificationBadge = (status) => {
    switch (status) {
      case 'PRODUCTION_READY':
        return (
          <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/50 rounded-lg">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            <span className="font-bold text-emerald-400">PRODUCTION READY</span>
          </div>
        );
      case 'CONDITIONAL':
        return (
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-500/50 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <span className="font-bold text-amber-400">CONDITIONAL</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/50 rounded-lg">
            <XCircle className="w-5 h-5 text-red-500" />
            <span className="font-bold text-red-400">NOT READY</span>
          </div>
        );
    }
  };

  const DomainCard = ({ title, icon: Icon, data, iconColor }) => (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm text-slate-300">
          <Icon className={`w-4 h-4 ${iconColor}`} />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-2">
          <span className={`text-2xl font-bold ${getStatusColor(data?.status)}`}>
            {data?.score?.toFixed(0) || 0}%
          </span>
          <span className={`text-xs px-2 py-1 rounded ${
            data?.status === 'excellent' ? 'bg-emerald-500/20 text-emerald-400' :
            data?.status === 'good' ? 'bg-blue-500/20 text-blue-400' :
            'bg-amber-500/20 text-amber-400'
          }`}>
            {data?.status?.toUpperCase()}
          </span>
        </div>
        {data?.issues?.length > 0 && (
          <div className="mt-2 space-y-1">
            {data.issues.slice(0, 2).map((issue, idx) => (
              <div key={idx} className="text-xs text-amber-400 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {issue}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const PhaseCard = ({ phase, data }) => (
    <div className="flex items-center justify-between py-2 border-b border-slate-700 last:border-0">
      <span className="text-sm text-slate-400">{phase.replace(/_/g, ' ').replace('phase ', 'Phase ')}</span>
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-300">{data?.score?.toFixed(0) || 0}%</span>
        {data?.status === 'passed' ? (
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        ) : (
          <XCircle className="w-4 h-4 text-red-500" />
        )}
      </div>
    </div>
  );

  if (loading && !healthData) {
    return (
      <div className="min-h-screen bg-slate-900 p-6 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-400">
          <RefreshCw className="w-5 h-5 animate-spin" />
          Loading system health data...
        </div>
      </div>
    );
  }

  if (error && !healthData) {
    return (
      <div className="min-h-screen bg-slate-900 p-6 flex items-center justify-center">
        <Card className="bg-red-500/10 border-red-500/50 max-w-md">
          <CardContent className="p-6 text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-red-400 mb-2">Error Loading Data</h3>
            <p className="text-slate-400 mb-4">{error}</p>
            <button 
              onClick={fetchHealthData}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white"
            >
              Retry
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Gauge className="w-7 h-7 text-cyan-500" />
              System Health Dashboard
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Real-time production readiness monitoring
            </p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-500">
              Last updated: {lastUpdated || 'Never'}
            </span>
            <button 
              onClick={fetchHealthData}
              disabled={loading}
              className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Certification Status */}
        <Card className="bg-gradient-to-r from-slate-800/80 to-slate-800/40 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm mb-2">Production Readiness Status</p>
                <div className="flex items-center gap-4">
                  <span className="text-4xl font-bold text-white">
                    {healthData?.overall_score?.toFixed(1) || 0}
                  </span>
                  <span className="text-slate-400 text-xl">/100</span>
                </div>
              </div>
              {getCertificationBadge(healthData?.certification_status)}
            </div>
          </CardContent>
        </Card>

        {/* Domain Scores */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <DomainCard 
            title="Data Consistency" 
            icon={Database}
            iconColor="text-cyan-500"
            data={healthData?.domains?.data_consistency} 
          />
          <DomainCard 
            title="Financial Viability" 
            icon={DollarSign}
            iconColor="text-emerald-500"
            data={healthData?.domains?.financial_viability} 
          />
          <DomainCard 
            title="Workforce Health" 
            icon={Users}
            iconColor="text-blue-500"
            data={healthData?.domains?.workforce_health} 
          />
          <DomainCard 
            title="Compliance" 
            icon={Shield}
            iconColor="text-purple-500"
            data={healthData?.domains?.compliance} 
          />
          <DomainCard 
            title="Performance" 
            icon={Activity}
            iconColor="text-amber-500"
            data={{
              score: 95,
              status: healthData?.domains?.system_performance?.status || 'excellent',
              issues: []
            }} 
          />
        </div>

        {/* Audit Phases */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-lg">9-Phase Audit Results</CardTitle>
            </CardHeader>
            <CardContent>
              {healthData?.audit_phases && Object.entries(healthData.audit_phases).map(([phase, data]) => (
                <PhaseCard key={phase} phase={phase} data={data} />
              ))}
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-lg">Critical Issues Fixed</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {healthData?.recent_issues_fixed?.map((issue, idx) => (
                <div key={idx} className="p-3 bg-slate-700/30 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-white text-sm">{issue.issue}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      issue.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                      issue.severity === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {issue.severity?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">{issue.fix_description}</p>
                  {issue.fixed && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-emerald-400">
                      <CheckCircle2 className="w-3 h-3" />
                      Fixed
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Active Alerts */}
        {healthData?.critical_alerts?.length > 0 && (
          <Card className="bg-amber-500/10 border-amber-500/50">
            <CardHeader>
              <CardTitle className="text-amber-400 text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Active Alerts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {healthData.critical_alerts.map((alert, idx) => (
                <div key={idx} className="p-3 bg-slate-800/50 rounded-lg flex items-start justify-between">
                  <div>
                    <span className="text-sm text-white font-medium">{alert.domain}: </span>
                    <span className="text-sm text-slate-300">{alert.message}</span>
                    <p className="text-xs text-slate-400 mt-1">Action: {alert.action}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    alert.type === 'critical' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {alert.type?.toUpperCase()}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SystemHealthDashboard;
